import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ArrowLeft, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import { CASE_STUDIES } from "@/data/content";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const study = CASE_STUDIES.find((c) => c.slug === slug);

  if (!study) return <Navigate to="/case-studies" replace />;

  const others = CASE_STUDIES.filter((c) => c.slug !== slug).slice(0, 2);

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative pt-16 md:pt-24 pb-16 overflow-hidden">
        <div className="container-x">
          <Reveal>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 text-bone-200/60 hover:text-clay-400 transition-colors mb-10"
              data-testid="case-detail-back"
            >
              <ArrowLeft size={14} />
              All case studies
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <Reveal>
                <p className="eyebrow mb-6">{study.industry}</p>
              </Reveal>
              <Reveal delay={80}>
                <h1
                  className="display-h1"
                  data-testid={`case-detail-title-${study.slug}`}
                >
                  {study.title}
                </h1>
              </Reveal>
            </div>
            <div className="md:col-span-4 md:pl-6 space-y-4 text-sm">
              <div>
                <p className="text-xs uppercase tracking-widest2 text-bone-200/50 mb-1">Client</p>
                <p className="text-bone-100">{study.client}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest2 text-bone-200/50 mb-1">Duration</p>
                <p className="text-bone-100">{study.duration}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest2 text-bone-200/50 mb-1">Services</p>
                <p className="text-bone-100">{study.services.join(" · ")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE */}
      <section className="pb-16">
        <div className="container-x">
          <Reveal>
            <div className="relative aspect-[21/9] rounded-lg overflow-hidden border border-white/10">
              <img src={study.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* METRICS STRIP */}
      <section className="border-y border-white/10 bg-ink-900/50 py-14 md:py-20">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-10">
          {study.metrics.map((m, i) => (
            <Reveal key={m.label} delay={i * 60}>
              <div>
                <div className="font-display text-4xl md:text-5xl text-clay-400">{m.value}</div>
                <div className="mt-2 text-xs uppercase tracking-widest2 text-bone-200/50">{m.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CHALLENGE + APPROACH */}
      <section className="py-24 md:py-32">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <p className="eyebrow mb-6">The challenge</p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="display-h3">Where they were.</h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={100}>
              <p className="body-lg">{study.challenge}</p>
            </Reveal>
          </div>
        </div>

        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-16 mt-24 md:mt-32">
          <div className="md:col-span-4">
            <Reveal>
              <p className="eyebrow mb-6">Our approach</p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="display-h3">What we did.</h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <ul className="space-y-6">
              {study.approach.map((step, i) => (
                <Reveal key={i} delay={i * 60}>
                  <li className="flex items-start gap-5 border-b border-white/10 pb-6">
                    <span className="font-display text-2xl text-clay-400 shrink-0 mt-1">
                      0{i + 1}
                    </span>
                    <p className="text-bone-100/90 leading-relaxed">{step}</p>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>

        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-16 mt-24 md:mt-32">
          <div className="md:col-span-4">
            <Reveal>
              <p className="eyebrow mb-6">The outcome</p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="display-h3">Where they ended up.</h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={100}>
              <p className="body-lg">{study.outcomes}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-20 md:py-32 bg-ink-900/40 border-y border-white/5">
        <div className="container-x max-w-4xl">
          <Reveal>
            <blockquote className="font-display text-3xl md:text-4xl leading-[1.2] tracking-tight text-bone-100">
              <span className="text-clay-400">"</span>
              {study.testimonial.quote}
              <span className="text-clay-400">"</span>
            </blockquote>
          </Reveal>
          <Reveal delay={100}>
            <div className="mt-10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-clay-500/20 border border-clay-400/40 flex items-center justify-center font-display text-clay-400">
                {study.testimonial.author
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </div>
              <div>
                <div className="text-bone-100 font-semibold">
                  {study.testimonial.author}
                </div>
                <div className="text-xs uppercase tracking-widest2 text-bone-200/50">
                  {study.testimonial.role}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* NEXT + CTA */}
      <section className="py-24 md:py-32">
        <div className="container-x">
          <div className="mb-16">
            <Reveal>
              <p className="eyebrow mb-6">Keep reading</p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="display-h2">More client stories.</h2>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24">
            {others.map((c, i) => (
              <Reveal key={c.slug} delay={i * 80}>
                <Link
                  to={`/case-studies/${c.slug}`}
                  className="group card-surface overflow-hidden flex flex-col h-full"
                  data-testid={`case-detail-related-${c.slug}`}
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={c.image}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/30 to-transparent" />
                    <div className="absolute top-6 left-6 text-xs uppercase tracking-widest2 text-clay-400">
                      {c.industry}
                    </div>
                  </div>
                  <div className="p-10">
                    <div className="flex items-baseline gap-4 mb-4">
                      <span className="font-display text-4xl text-clay-400">{c.metric}</span>
                      <span className="text-xs uppercase tracking-widest2 text-bone-200/50">
                        {c.metricLabel}
                      </span>
                    </div>
                    <h3 className="display-h3 mb-4">{c.title}</h3>
                    <span className="link-arrow text-sm font-semibold uppercase tracking-widest2">
                      Read the story
                      <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="border border-white/10 rounded-lg p-10 md:p-16 bg-ink-800/40 backdrop-blur text-center">
              <p className="eyebrow justify-center mb-6">Your story next</p>
              <h2 className="display-h2 mb-8">
                Build a case study we'd be proud to publish.
              </h2>
              <Link to="/contact" className="btn-primary">
                Start the conversation
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
